import { useMemo, useRef, useState, type ClipboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MAX_UPLOAD_BYTES, type TicketDetail, type TicketFile } from '@erp/shared';
import { Bot, Download, Lock, Paperclip, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/misc';
import { cn, formatDateTime } from '@/lib/utils';
import { ticketCommentsQuery, ticketFilesQuery } from './tickets-queries';
import { downloadTicketFile, uploadTicketFile, useAddComment } from './tickets-mutations';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { TemplateInsertMenu } from '@/features/models/template-insert-menu';
import { errorMessage, initials, isTicketMirrored } from './ticket-format';
import { MirroredNotice } from './mirrored-badge';
import { AiSuggestButton, useAiActive } from './ai-actions';
import { MentionText, mentionErrorMessage, useMentionTextarea } from './mentions';
import { pastedAttachmentNames } from './attachment-paste';

const MAX_LABEL = `${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB`;

/** Aba "Comunicação": composer (Interno via Switch, anexos, e-mail) + feed. */
export function ComunicacaoTab({ ticket }: { ticket: TicketDetail }) {
  const ticketNumber = ticket.ticketNumber;
  const qc = useQueryClient();
  const commentsQ = useQuery(ticketCommentsQuery(ticketNumber));
  const filesQ = useQuery(ticketFilesQuery(ticketNumber));
  const addComment = useAddComment(ticketNumber);

  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /** File → fileId dos uploads já concluídos: no retry só sobe o que falta. */
  const uploadedRef = useRef(new Map<File, number>());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // spec 037 (PR C): o @ abre a equipe — só na comunicação interna
  const mention = useMentionTextarea(ticketNumber, internal, body, setBody);

  const feed = useMemo(
    () =>
      [...(commentsQ.data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [commentsQ.data]
  );

  /** Anexos por comentário (commentId null = anexo solto, fica fora do feed). */
  const filesByComment = useMemo(() => {
    const map = new Map<number, TicketFile[]>();
    for (const f of filesQ.data ?? []) {
      if (f.commentId === null) continue;
      const arr = map.get(f.commentId);
      if (arr) arr.push(f);
      else map.set(f.commentId, [f]);
    }
    return map;
  }, [filesQ.data]);

  // Checkbox de e-mail só faz sentido em comentário público com solicitante, e
  // com texto: o e-mail não leva anexo (spec 068, D4; a API recusa sem texto).
  const hasText = body.trim().length > 0;
  const canSendEmail = !internal && ticket.requestorEmail !== null && hasText;
  // spec 068 (D4): sai com texto, com anexo, ou com os dois
  const canSubmit = hasText || pendingFiles.length > 0;

  // v11: "Sugerir resposta" — só com a integração de IA ativa (GET /ai/status).
  const aiActive = useAiActive();

  const addFiles = (list: FileList | readonly File[] | null) => {
    if (!list) return;
    setError(null);
    const accepted: File[] = [];
    for (const file of Array.from(list)) {
      if (file.size > MAX_UPLOAD_BYTES) {
        setError(`"${file.name}" excede o limite de ${MAX_LABEL} por arquivo.`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) setPendingFiles((prev) => [...prev, ...accepted]);
  };

  const busy = uploading || addComment.isPending;

  /**
   * spec 068 (D4): Ctrl+V de um print (ou de arquivos copiados) dentro do campo
   * vira anexo, com o nome `print-…`; colar texto continua sendo do campo.
   */
  const pasteIntoField = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files);
    const names = pastedAttachmentNames(files, new Date());
    if (!names) return;
    e.preventDefault();
    addFiles(files.map((file, i) => new File([file], names[i] ?? file.name, { type: file.type })));
  };

  const submit = async () => {
    if (!canSubmit || busy) return;
    setError(null);

    // 1) sobe os anexos (pulando os já subidos num envio anterior que falhou);
    // 2) envia o comentário com os fileIds vinculados.
    let fileIds: number[] | undefined;
    if (pendingFiles.length > 0) {
      setUploading(true);
      try {
        const uploaded: number[] = [];
        for (const file of pendingFiles) {
          let fileId = uploadedRef.current.get(file);
          if (fileId === undefined) {
            const created = await uploadTicketFile(ticketNumber, file);
            fileId = created.id;
            uploadedRef.current.set(file, fileId);
          }
          uploaded.push(fileId);
        }
        fileIds = uploaded;
      } catch (e) {
        setError(errorMessage(e));
        setUploading(false);
        void qc.invalidateQueries({ queryKey: ['erp', 'tickets', 'files', { ticketNumber }] });
        return;
      }
      setUploading(false);
    }

    const trimmed = body.trim();
    const mentions = internal ? mention.mentions : [];
    addComment.mutate(
      {
        body: trimmed,
        internal,
        ...(fileIds ? { fileIds } : {}),
        ...(canSendEmail && sendEmail ? { sendEmail: true } : {}),
        ...(mentions.length > 0 ? { mentions } : {}),
      },
      {
        onSuccess: () => {
          setBody('');
          setInternal(false);
          setSendEmail(false);
          setPendingFiles([]);
          uploadedRef.current.clear();
        },
        onError: (e) =>
          setError(mentionErrorMessage((e as { error?: string } | null)?.error) ?? errorMessage(e)),
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* spec 011: comentário em ticket espelhado não chegaria ao TiFlux — a
          API recusa (409); o histórico abaixo continua completo */}
      {isTicketMirrored(ticket) ? (
        <MirroredNotice>
          Ticket espelhado do TiFlux: responda pelo TiFlux. As respostas de lá aparecem aqui na
          próxima sincronização.
        </MirroredNotice>
      ) : (
        <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
          <h2 className="mb-3 text-base font-semibold">Nova comunicação</h2>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div>
              <Textarea
                ref={mention.ref}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  mention.onCaret(e.target);
                }}
                onKeyDown={(e) => void mention.onKeyDown(e)}
                onClick={(e) => mention.onCaret(e.currentTarget)}
                onKeyUp={(e) => mention.onCaret(e.currentTarget)}
                onPaste={pasteIntoField}
                placeholder={internal ? 'Escreva uma comunicação interna… (@ menciona alguém da equipe)' : 'Escreva uma comunicação…'}
                className="min-h-24"
                data-tour="mencao-editor"
              />
              {mention.panel}
            </div>
            {mention.summary}

            {pendingFiles.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {pendingFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-xs"
                  >
                    <Paperclip className="size-3 shrink-0 text-muted-foreground" />
                    <span className="max-w-48 truncate" title={file.name}>
                      {file.name}
                    </span>
                    <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      title="Remover anexo"
                      className="text-muted-foreground transition-colors hover:text-destructive"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex cursor-pointer items-center gap-2">
                  <Switch checked={internal} onCheckedChange={setInternal} id="comm-internal" />
                  <Label htmlFor="comm-internal" className="cursor-pointer text-sm font-normal">
                    Interno
                  </Label>
                  {internal ? (
                    <span className="text-xs text-amber-600">visível apenas para a equipe</span>
                  ) : null}
                </label>

                {canSendEmail ? (
                  <label className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      id="comm-send-email"
                      checked={sendEmail}
                      onCheckedChange={(v) => setSendEmail(v === true)}
                    />
                    <Label htmlFor="comm-send-email" className="cursor-pointer text-sm font-normal">
                      Enviar por e-mail ao solicitante
                    </Label>
                  </label>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                {aiActive ? (
                  <AiSuggestButton
                    ticketNumber={ticketNumber}
                    disabled={busy}
                    onSuggested={(text) =>
                      // Mesma regra do menu de modelos: acrescenta ao rascunho
                      // existente (o texto fica editável antes do envio).
                      setBody((prev) => (prev.trim() ? `${prev}\n${text}` : text))
                    }
                  />
                ) : null}
                <TemplateInsertMenu
                  kind="responses"
                  ticket={ticket}
                  onInsert={(text) =>
                    setBody((prev) => (prev.trim() ? `${prev}\n${text}` : text))
                  }
                />
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  ou cole um print (Ctrl+V)
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  title={`Anexar arquivos (até ${MAX_LABEL} cada)`}
                  onClick={() => inputRef.current?.click()}
                >
                  <Paperclip />
                  Anexar
                </Button>
                <Button type="submit" size="sm" disabled={!canSubmit || busy}>
                  {busy ? <Spinner className="size-4 border-primary-foreground" /> : <Send />}
                  Enviar
                </Button>
              </div>
            </div>
          </form>
        </section>
      )}

      {commentsQ.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      ) : commentsQ.isError ? (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
          Erro ao carregar as comunicações.
        </p>
      ) : feed.length === 0 ? (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
          Nenhuma comunicação ainda.
        </p>
      ) : (
        <div className="space-y-3">
          {feed.map((c) => (
            <article
              key={c.id}
              className={cn(
                'rounded-lg border bg-card p-4 text-card-foreground shadow-sm',
                // spec 022: a nota do bot é interna, mas não é a equipe — cor própria
                c.authorKind === 'bot'
                  ? 'border-l-4 border-l-violet-400'
                  : c.internal && 'border-l-4 border-l-amber-400'
              )}
            >
              <header className="mb-2 flex flex-wrap items-center gap-2">
                <Avatar size="sm">
                  {c.authorKind === 'bot' ? (
                    <AvatarFallback className="bg-violet-100 text-violet-700">
                      <Bot className="size-3.5" />
                    </AvatarFallback>
                  ) : (
                    <AvatarFallback className="bg-primary/15 text-[10px] font-semibold text-primary">
                      {initials(c.authorName)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <span className="text-sm font-semibold">{c.authorName}</span>
                {c.authorKind === 'bot' ? (
                  <span className="inline-flex items-center gap-1 rounded-[4px] border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                    <Bot className="size-3" />
                    Nota do bot
                  </span>
                ) : null}
                {c.internal ? (
                  <span className="inline-flex items-center gap-1 rounded-[4px] border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                    <Lock className="size-3" />
                    Comunicação interna
                  </span>
                ) : null}
                <time className="ml-auto text-xs text-muted-foreground">
                  {formatDateTime(c.createdAt)}
                </time>
              </header>
              {/* spec 068 (D4): a comunicação só com o print colado não tem texto */}
              {c.body.trim() ? (
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                  <MentionText body={c.body} mentions={c.mentions} />
                </div>
              ) : null}
              <CommentAttachments
                files={filesByComment.get(c.id) ?? []}
                separated={c.body.trim().length > 0}
              />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

/** Chips de anexo de um comentário — download autenticado (fetch + blob). */
function CommentAttachments({ files, separated }: { files: TicketFile[]; separated: boolean }) {
  const [error, setError] = useState<string | null>(null);
  if (files.length === 0) return null;

  const download = (file: TicketFile) => {
    setError(null);
    downloadTicketFile(file).catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Falha no download')
    );
  };

  return (
    <div className={cn('space-y-1', separated && 'mt-3 border-t pt-2')}>
      <div className="flex flex-wrap gap-2">
        {files.map((file) => (
          <button
            key={file.id}
            type="button"
            title={`Baixar ${file.fileName}`}
            onClick={() => download(file)}
            className="group inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-xs transition-colors hover:border-primary/50 hover:bg-accent"
          >
            <Paperclip className="size-3 shrink-0 text-muted-foreground" />
            <span className="max-w-48 truncate font-medium" title={file.fileName}>
              {file.fileName}
            </span>
            <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
            <Download className="size-3 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
          </button>
        ))}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
